import {
	CheckFinding,
	CheckFindingSeverity,
	CheckReport,
	InvoiceBatchDryRun,
} from "../models/InvoiceBatch";
import { InvoiceExportType } from "../models/Organisation";

/**
 * Whether "Testkör" is offered. Both Visma.net and Fortnox support a dry run;
 * an organisation without an explicit export type invoices through Fortnox
 * (job-service's default), so it is offered there too.
 */
export function isDryRunSupported(exportType?: InvoiceExportType | string): boolean {
	return (exportType ?? "FORTNOX") === "FORTNOX" || exportType === "VISMA_NET_BEMANNIA";
}

export function isDryRunRunning(dryRun?: InvoiceBatchDryRun): boolean {
	return dryRun?.status === "RUNNING";
}

export interface DryRunSummary {
	invoicesChecked: number;
	/** Invoices with at least one error finding. */
	invoicesWithErrors: number;
	/** Invoices with warnings but no error. */
	invoicesWithWarnings: number;
	batchErrors: number;
	batchWarnings: number;
	/** Total error findings, batch-level and per invoice. */
	errorCount: number;
	warningCount: number;
}

export function summarizeDryRun(report: CheckReport): DryRunSummary {
	const severityByInvoice = invoiceSeverities(report.findings);
	const summary: DryRunSummary = {
		invoicesChecked: report.invoicesChecked,
		invoicesWithErrors: 0,
		invoicesWithWarnings: 0,
		batchErrors: 0,
		batchWarnings: 0,
		errorCount: 0,
		warningCount: 0,
	};

	for (const severity of severityByInvoice.values()) {
		if (severity === "error") summary.invoicesWithErrors++;
		else summary.invoicesWithWarnings++;
	}

	for (const finding of report.findings) {
		if (finding.severity === "error") summary.errorCount++;
		else summary.warningCount++;

		if (!finding.invoiceId) {
			if (finding.severity === "error") summary.batchErrors++;
			else summary.batchWarnings++;
		}
	}

	return summary;
}

/** Errors before warnings, otherwise in the order the backend reported them. */
function bySeverity(a: CheckFinding, b: CheckFinding) {
	return (a.severity === "error" ? 0 : 1) - (b.severity === "error" ? 0 : 1);
}

export interface DryRunInvoiceFindings {
	invoiceId: string;
	/** The worst severity among the invoice's findings. */
	severity: CheckFindingSeverity;
	findings: CheckFinding[];
}

/**
 * Batch-level findings (no `invoiceId`) and per-invoice groups. Groups follow
 * `invoiceOrder` (the batch's row order) when given, so the panel reads in the
 * same order as the table; invoices missing from it come last.
 */
export function groupDryRunFindings(
	findings: CheckFinding[],
	invoiceOrder: string[] = []
): { batchLevel: CheckFinding[]; byInvoice: DryRunInvoiceFindings[] } {
	const batchLevel: CheckFinding[] = [];
	const groups = new Map<string, CheckFinding[]>();

	for (const finding of findings) {
		if (!finding.invoiceId) {
			batchLevel.push(finding);
			continue;
		}

		const group = groups.get(finding.invoiceId) ?? [];
		group.push(finding);
		groups.set(finding.invoiceId, group);
	}

	const position = new Map(invoiceOrder.map((id, index) => [id, index]));
	const rank = (id: string) => position.get(id) ?? Number.MAX_SAFE_INTEGER;

	const byInvoice = [...groups.entries()]
		.map(([invoiceId, group]) => {
			const sorted = [...group].sort(bySeverity);
			return { invoiceId, severity: sorted[0].severity, findings: sorted };
		})
		// Array.prototype.sort is stable, so unknown invoices keep report order
		.sort((a, b) => rank(a.invoiceId) - rank(b.invoiceId));

	return { batchLevel: batchLevel.sort(bySeverity), byInvoice };
}

function invoiceSeverities(findings: CheckFinding[]): Map<string, CheckFindingSeverity> {
	const severities = new Map<string, CheckFindingSeverity>();

	for (const { invoiceId, severity } of findings) {
		if (!invoiceId) continue;
		if (severities.get(invoiceId) !== "error") severities.set(invoiceId, severity);
	}

	return severities;
}

/**
 * Worst finding severity per invoice, for the markers on the invoice rows.
 * Empty unless the latest run completed and is still current: an outdated
 * result may point at invoices that have since changed.
 */
export function dryRunInvoiceMarkers(
	dryRun?: InvoiceBatchDryRun
): Map<string, CheckFindingSeverity> {
	if (dryRun?.status !== "DONE" || dryRun.outdated || !dryRun.report) return new Map();

	return invoiceSeverities(dryRun.report.findings);
}

/**
 * "Skapa fakturor" asks for confirmation first when the latest dry run
 * completed, is still current and found errors. It never blocks (D4).
 */
export function shouldConfirmProcessAfterDryRun(dryRun?: InvoiceBatchDryRun): boolean {
	return (
		dryRun?.status === "DONE" &&
		!dryRun.outdated &&
		!!dryRun.report?.findings.some((finding) => finding.severity === "error")
	);
}

export function hasPlannedWrites(report: CheckReport): boolean {
	const writes = report.plannedWrites;

	return (
		!!writes &&
		(writes.customersToCreate?.length > 0 ||
			writes.customersToUpdate?.length > 0 ||
			writes.articlesToCreate?.length > 0 ||
			writes.subaccountsToCreate?.length > 0)
	);
}

/** DOM id of an invoice row in the batch table, the target of the panel's links. */
export function invoiceRowElementId(invoiceId: string) {
	return `invoice-row-${invoiceId}`;
}

/**
 * Message for a failed dry-run start: 409 `DRY_RUN_IN_PROGRESS` when someone
 * else already started one, otherwise a generic failure.
 */
export function dryRunStartErrorKey(error: unknown) {
	return (error as { error?: { code?: string } })?.error?.code === "DRY_RUN_IN_PROGRESS"
		? "invoiceBatch.dryRun.alreadyRunning"
		: "invoiceBatch.dryRun.startFailed";
}
